import type { PropsWithChildren } from 'react'
import { createContext, useContext, useMemo } from 'react'
import * as React from 'react'

import { getMapboxAccessToken, MAPBOX_ACCESS_TOKEN_USE_CASES } from 'lib/maps/mapboxAccessToken'
import type { MapboxAccessTokenUseCase } from 'lib/maps/mapboxAccessToken'

interface MapboxContextProps {
  isLoaded: boolean
  accessToken?: string
}

const MapboxContext = createContext<MapboxContextProps>({
  isLoaded: false,
  accessToken: undefined,
})

export const useMapboxContext = () => {
  return useContext(MapboxContext)
}

interface EnsureMapboxLoadedProps {
  loadingElement?: React.ReactNode
  children: React.ReactNode
}

export function EnsureMapboxLoaded({ loadingElement = null, children }: EnsureMapboxLoadedProps) {
  const { isLoaded } = useMapboxContext()

  return <>{isLoaded ? children : loadingElement}</>
}

interface MapboxProviderProps extends PropsWithChildren {
  useCase?: MapboxAccessTokenUseCase
}

export function MapboxProvider({
  useCase = MAPBOX_ACCESS_TOKEN_USE_CASES.default,
  children,
}: MapboxProviderProps) {
  const accessToken = getMapboxAccessToken(useCase)

  const value = useMemo<MapboxContextProps>(() => ({
    isLoaded: Boolean(accessToken),
    accessToken,
  }), [accessToken])

  return (
    <MapboxContext.Provider value={value}>
      {children}
    </MapboxContext.Provider>
  )
}
